import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col, Card, Spinner, Badge, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCalendarAlt, 
  faArrowLeft, 
  faFilePdf, 
  faFileInvoice, 
  faMoneyBill,
  faTag
} from "@fortawesome/free-solid-svg-icons";
import FeatherIcon from "feather-icons-react";
import Button from "components/base/Button";
import { useLazyQuery } from "@apollo/client";
import { GET_INVOICE_DETAILS_BY_REF } from "api/graphql/queries/invoice/getInvoiceDetailsByRef";
import { toast } from "react-toastify";

// Define InvoiceType enum
export enum InvoiceType {
  Logistic = "LOGISTIC",
  Penalty = "PENALTY",
  All = "ALL"
}

const InvoiceDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceRef } = useParams<{ invoiceRef: string }>();
  
  // Get type and journalId from location state if available
  const { type, journalId } = location.state || {};
  
  // Define interface for invoice lines
  interface InvoiceLine {
    label: string;
    amount: number;
  }

  // Define interface for invoice details
  interface InvoiceDetails {
    ref: string;
    date: string;
    invoice_lines: InvoiceLine[];
    total: number;
    type?: InvoiceType;
    journalId?: number;
  }
  
  // Local state for invoice details
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails | null>(null);
  
  // GraphQL query hook
  const [getInvoiceDetails, { loading, error: graphQlError }] = useLazyQuery(GET_INVOICE_DETAILS_BY_REF, {
    onCompleted: (data: any) => {
      console.log('GraphQL response:', data);
      if (data?.getInvoiceDetailsByRef?.status === 'success' && data?.getInvoiceDetailsByRef?.details) {
        setInvoiceDetails({
          ...data.getInvoiceDetailsByRef.details,
          type: type as InvoiceType,
          journalId: journalId
        });
      } else {
        toast.error('Failed to fetch invoice details');
      }
    },
    onError: (error: any) => {
      console.error('GraphQL error:', error);
      toast.error(`Error loading invoice: ${error.message}`);
    }
  });
  
  // Format date in a user-friendly way
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    
    try {
      const date = new Date(dateStr);
      
      // Format date: Mar 27, 2025
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return dateStr;
    }
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
      maximumFractionDigits: 3
    }).format(amount);
  };
  
  useEffect(() => {
    if (invoiceRef) {
      console.log(`Fetching invoice ${invoiceRef} details with type: ${type}, journalId: ${journalId}`);
      
      const variables: any = {
        ref: invoiceRef
      };
      
      if (type) {
        variables.type = type;
      }
      
      if (journalId) {
        variables.journal_id = journalId;
      }
      
      getInvoiceDetails({ variables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceRef, type, journalId]);
  
  const handleBack = () => {
    navigate("/invoices");
  };
  
  const handleDownloadPdf = () => {
    console.log("Download PDF for invoice:", invoiceRef);
    // Implement actual PDF download here
  };
  
  // Get badge variant based on invoice type
  const getInvoiceTypeBadgeVariant = (invoiceType?: InvoiceType): string => {
    switch (invoiceType) {
      case InvoiceType.Logistic:
        return "primary";
      case InvoiceType.Penalty:
        return "warning";
      default:
        return "secondary";
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </div>
    );
  }
  
  // Error state
  if (graphQlError || !invoiceDetails) {
    return (
      <div className="mb-9">
        <Button
          variant="link"
          className="text-decoration-none p-0 mb-4"
          onClick={handleBack}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          {t("invoices.details.backToInvoices")}
        </Button>
        
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>{t("invoices.details.errorLoading")}</Alert.Heading>
          <p>{graphQlError?.message || t("invoices.details.notFound")}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button
              variant="outline-danger"
              onClick={() => window.location.reload()}
            >
              {t("common.retry", "Retry")}
            </Button>
          </div>
        </Alert>
      </div>
    );
  }
  
  // Get variant based on type
  const badgeVariant = getInvoiceTypeBadgeVariant(invoiceDetails.type);
 
  // Force type based on reference prefix
  let invoiceType: InvoiceType;
  
  if (invoiceRef && invoiceRef.startsWith('JVSL/')) {
    // If ref starts with JVSL, it's ALWAYS a penalty invoice regardless of other settings
    console.log('Setting as PENALTY based on JVSL prefix');
    invoiceType = InvoiceType.Penalty;
  } else if (journalId === 74) {
    // Use journal ID if available (74 is penalty)
    console.log('Setting as PENALTY based on journal ID 74');
    invoiceType = InvoiceType.Penalty;
  } else if (invoiceDetails.type) {
    // Use explicit type if available
    console.log('Using explicit type:', invoiceDetails.type);
    invoiceType = invoiceDetails.type;
  } else {
    // Default to logistic
    console.log('Defaulting to LOGISTIC');
    invoiceType = InvoiceType.Logistic;
  }
  
  console.log('Final invoice type:', invoiceType);
  
  return (
    <div className="mb-9">
      {/* Page Header */}
      <h2 className="mb-1">
        {t("invoices.details.title")} <span>#{invoiceRef}</span>
      </h2>
      
      <div className="d-flex flex-wrap flex-between-center mb-5 gap-3">
        <Button
          variant="link"
          className="text-decoration-none p-0"
          onClick={handleBack}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          {t("invoices.details.backToInvoices")}
        </Button>
        
        <Button
          variant="phoenix-primary"
          onClick={handleDownloadPdf}
          className="px-3 py-2"
          startIcon={<FontAwesomeIcon icon={faFilePdf} />}
        >
          {t("invoices.buttons.download")}
        </Button>
      </div>
      
      {/* Invoice Status Card */}
      <Card className={`border-top border-0 border-4 ${invoiceType === InvoiceType.Penalty ? 'border-warning' : 'border-primary'} shadow-sm mb-4`}>
        <Card.Body className="d-flex align-items-center p-3">
          <div className={`p-3 ${invoiceType === InvoiceType.Penalty ? 'bg-warning-subtle' : 'bg-primary-subtle'} rounded-3 me-4`}>
            <FeatherIcon 
              icon={invoiceType === InvoiceType.Penalty ? "alert-triangle" : "truck"} 
              size={20} 
              className={invoiceType === InvoiceType.Penalty ? 'text-warning' : 'text-primary'} 
            />
          </div>
          <div>
            <h5 className="mb-2 d-flex align-items-center">
              {t("invoices.tableHeaders.type")}: 
              <span className={`${invoiceType === InvoiceType.Penalty ? 'text-warning' : 'text-primary'} ms-2 d-flex align-items-center fw-bold`}>
                {invoiceType === InvoiceType.Penalty ? 
                  t("invoices.types.penalty") : 
                  t("invoices.types.logistic")}
              </span>
            </h5>
            <p className="mb-0 text-body-secondary d-flex align-items-center">
              {t("invoices.reference")}: 
              <span className="ms-2 fw-semibold">
                {invoiceRef}
              </span>
            </p>
          </div>
        </Card.Body>
      </Card>
      
      <Row className="g-5 gy-7">
        <Col xs={12} xl={8} xxl={9}>
          
          {/* Invoice Items Table */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-body-light p-3">
              <h5 className="mb-0 d-flex align-items-center">
                <FeatherIcon icon="list" size={16} className="text-secondary me-2" />
                {t("invoices.details.items")}
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-body-secondary">
                    <tr>
                      <th className="py-3 ps-4 border-0 fw-semibold text-nowrap">
                        {t("invoices.details.itemName")}
                      </th>
                      <th className="py-3 pe-4 border-0 fw-semibold text-end text-nowrap">
                        {t("invoices.details.amount")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceDetails.invoice_lines.length > 0 ? (
                      <>
                        {/* Render all lines except "Timbre Fiscale sur vente FBW" first */}
                        {invoiceDetails.invoice_lines
                          .filter((line: InvoiceLine) => line.label !== "Timbre Fiscale sur vente FBW")
                          .map((line: InvoiceLine, index: number) => (
                            <tr key={index} className="border-hover-primary">
                              <td className="ps-4 py-3 align-middle">
                                <span className="fs-9">{line.label}</span>
                              </td>
                              <td className={`pe-4 py-3 align-middle text-end fw-semibold fs-9`}>
                                {formatCurrency(line.amount)}
                              </td>
                            </tr>
                          ))
                        }
                        
                        {/* Render "Timbre Fiscale sur vente FBW" last if it exists */}
                        {invoiceDetails.invoice_lines
                          .filter((line: InvoiceLine) => line.label === "Timbre Fiscale sur vente FBW")
                          .map((line: InvoiceLine, index: number) => (
                            <tr key={`timbre-${index}`} className="border-hover-primary">
                              <td className="ps-4 py-3 align-middle">
                                <span className="fs-9">{line.label}</span>
                              </td>
                              <td className={`pe-4 py-3 align-middle text-end fw-semibold fs-9`}>
                                {formatCurrency(line.amount)}
                              </td>
                            </tr>
                          ))
                        }
                      </>
                    ) : (
                      <tr>
                        <td colSpan={2} className="text-center py-5">
                          <div className="mb-2">
                            <FeatherIcon icon="file-text" size={24} className="text-body-secondary" />
                          </div>
                          <p className="mb-0 text-body-secondary">
                            {t("invoices.details.noItems")}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
            {invoiceDetails.invoice_lines.length > 0 && (
              <Card.Footer className="bg-body-terlighttiary p-0">
                <div className="d-flex justify-content-between align-items-center p-3">
                  <span className="fw-bold fs-8">{t("invoices.details.total")}</span>
                  <span className={`fw-bold ${invoiceType === InvoiceType.Penalty ? 'text-warning' : 'text-primary'} fs-7`}>
                    {formatCurrency(invoiceDetails.total)}
                  </span>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
        
        <Col xs={12} xl={4} xxl={3}>
          {/* Invoice Summary Card */}
          <Card className="shadow-sm border-0" style={{ position: "sticky", top: "20px" }}>
            <Card.Header className="bg-body-light p-3">
              <h5 className="mb-0 d-flex align-items-center">
                <FeatherIcon icon="file-text" size={16} className="text-secondary me-2" />
                {t("invoices.details.title")} {t("orderDetails.orderSummary")}
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-body-secondary fs-9">{t("invoices.reference")}</span>
                <span className="fw-semibold fs-9">{invoiceRef}</span>
              </div>
              
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-body-secondary fs-9">{t("invoices.tableHeaders.date")}</span>
                <span className="fw-semibold fs-9">{formatDate(invoiceDetails.date)}</span>
              </div>
              
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-body-secondary fs-9">{t("invoices.tableHeaders.type")}</span>
                <span 
                  className={`badge badge-phoenix badge-phoenix-${invoiceType === InvoiceType.Penalty ? 'warning' : 'primary'} py-2 px-3`}
                >
                  {invoiceType === InvoiceType.Penalty ? 
                    t("invoices.types.penalty") : 
                    t("invoices.types.logistic")}
                </span>
              </div>
              
              <hr />
              
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold fs-8">{t("invoices.tableHeaders.amount")}</span>
                <span className={`fw-bold ${invoiceType === InvoiceType.Penalty ? 'text-warning' : 'text-primary'} fs-7`}>
                  {formatCurrency(invoiceDetails.total)}
                </span>
              </div>
              
              {/* <div className="mt-4">
                <Button
                  variant="phoenix-primary"
                  onClick={handleDownloadPdf}
                  className="w-100"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                >
                  {t("invoices.buttons.download")}
                </Button>
              </div> */}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InvoiceDetails;
